<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * List item label with caption, header, and line-clamping variants.
 *
 * @status recommended
 * @group lists-tables
 * @useWhen Rendering label text, supporting text, or section headers inside an HrItem
 * @within hr-list hr-item
 * @see https://quasar.dev/vue-components/list-and-list-items Quasar q-item-label API
 */
defineOptions({ name: 'HrItemLabel' });

const props = defineProps({
  /**
   * Renders as supporting text — smaller, muted, below the headline.
   * Maps to M3's "supporting text" role (body-medium, on-surface-variant).
   */
  caption: {
    type: Boolean,
    default: false,
  },
  /**
   * Renders as a list section header above a group of items.
   * Maps to M3's label-medium type role.
   */
  header: {
    type: Boolean,
    default: false,
  },
  /**
   * Clamps the label text to this many lines with an ellipsis.
   */
  lines: {
    type: Number,
    default: null,
  },
  /**
   * Legacy props passed through to q-item-label for backwards compatibility with the legacy
   * theme. Ignored when a design system theme is active.
   * @deprecated Remove once the legacy theme is removed.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const effectiveProps = computed(() => {
  const base = {
    caption: props.caption,
    header: props.header,
    ...(props.lines !== null && { lines: props.lines }),
  };
  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>

<template>
  <q-item-label
    v-bind="effectiveProps"
    :class="hasTheme ? 'hr-item-label' : undefined"
  >
    <!-- @slot Label text or custom content. -->
    <slot />
  </q-item-label>
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* ── Headline (default) ─────────────────────────────────────────── */
.hr-item-label {
  color: var(--hr-on-container, var(--app-on-surface));
  font-size:      var(--app-type-body-large-size);
  line-height:    var(--app-type-body-large-line-height);
  font-weight:    var(--app-type-body-large-weight);
  letter-spacing: var(--app-type-body-large-tracking);
}

/* ── Supporting text (caption) ──────────────────────────────────── */
.hr-item-label.q-item__label--caption {
  color: var(--hr-on-container, var(--app-on-surface-variant));
  font-size:      var(--app-type-body-medium-size);
  line-height:    var(--app-type-body-medium-line-height);
  font-weight:    var(--app-type-body-medium-weight);
  letter-spacing: var(--app-type-body-medium-tracking);
}

/* ── Section header ─────────────────────────────────────────────── */
.hr-item-label.q-item__label--header {
  color: var(--hr-on-container, var(--app-on-surface-variant));
  font-size:      var(--app-type-label-medium-size);
  line-height:    var(--app-type-label-medium-line-height);
  font-weight:    var(--app-type-label-medium-weight);
  letter-spacing: var(--app-type-label-medium-tracking);
  text-transform: none;
  padding: 12px 16px 4px;
}

</style>
